import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../ui/button';
import { Ship, Plane, Calculator, ArrowRight, CheckCircle, Clock, X, Ruler } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DiscountTimer = () => {
  const [left, setLeft] = useState(0);
  useEffect(() => {
    const tick = () => {
      const now = new Date();
      const midnight = new Date(now);
      midnight.setHours(24, 0, 0, 0);
      setLeft(Math.max(0, Math.floor((midnight - now) / 1000)));
    };
    tick();
    const t = setInterval(tick, 1000);
    return () => clearInterval(t);
  }, []);
  const h = String(Math.floor(left / 3600)).padStart(2, '0');
  const m = String(Math.floor((left % 3600) / 60)).padStart(2, '0');
  const s = String(left % 60).padStart(2, '0');
  return (
    <div className="flex items-center gap-2 mt-2" data-testid="discount-timer">
      <Clock className="w-4 h-4 text-amber-300 animate-pulse" />
      <span className="text-amber-200/90 text-xs font-semibold uppercase tracking-wide">Пропозиція діє ще:</span>
      <div className="flex items-center gap-1 font-mono font-bold text-white">
        <span className="bg-slate-900/70 border border-amber-400/40 rounded-md px-1.5 py-0.5 text-sm tabular-nums">{h}</span>
        <span className="text-amber-300">:</span>
        <span className="bg-slate-900/70 border border-amber-400/40 rounded-md px-1.5 py-0.5 text-sm tabular-nums">{m}</span>
        <span className="text-amber-300">:</span>
        <span className="bg-slate-900/70 border border-amber-400/40 rounded-md px-1.5 py-0.5 text-sm tabular-nums">{s}</span>
      </div>
    </div>
  );
};

export const TariffsSection = () => {
  const [cats, setCats] = useState(null);
  const [weight, setWeight] = useState(50);
  const [showDiscount, setShowDiscount] = useState(false);

  useEffect(() => {
    axios.get(`${API}/tariff-categories`)
      .then(res => setCats(res.data.categories))
      .catch(() => setCats(null));
  }, []);

  // Dwell timer: show discount banner after user stops changing weight for N seconds
  useEffect(() => {
    if (!cats?.discount?.enabled) return;
    setShowDiscount(false);
    const seconds = parseInt(cats.discount.dwell_seconds) || 5;
    const t = setTimeout(() => setShowDiscount(true), seconds * 1000);
    return () => clearTimeout(t);
  }, [weight, cats]);

  // Track impression once when banner becomes visible
  useEffect(() => {
    if (showDiscount && cats?.discount?.enabled) {
      axios.post(`${API}/discount-events`, {
        event: 'shown',
        weight,
        discount_percent: cats.discount.percent
      }).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showDiscount]);

  const handleDiscountClick = () => {
    if (!cats?.discount?.enabled) return;
    axios.post(`${API}/discount-events`, {
      event: 'clicked',
      weight,
      discount_percent: cats.discount.percent
    }).catch(() => {});
  };

  if (!cats) return null;
  const air = cats.air;
  const sea = cats.sea;

  const findTier = (rates, w) => {
    if (!rates || !rates.length) return null;
    for (const r of rates) {
      if (w >= r.min_kg && w < r.max_kg) return r;
    }
    return rates[rates.length - 1];
  };

  const airTier = findTier(air.rates, weight);
  const seaTier = findTier(sea.rates, weight);
  const airTotal = airTier ? airTier.price_per_kg * weight : 0;
  const seaTotal = seaTier ? seaTier.price_per_kg * weight : 0;
  const cheaper = airTotal < seaTotal ? 'air' : (seaTotal < airTotal ? 'sea' : null);
  const savings = Math.abs(airTotal - seaTotal);

  return (
    <section id="tariffs" className="py-20 md:py-28 bg-[#0a0f1c] relative overflow-hidden">
      <div className="absolute top-0 left-0 w-[600px] h-[600px] bg-amber-500/5 rounded-full blur-[150px]" />
      <div className="absolute bottom-0 right-0 w-[500px] h-[500px] bg-cyan-500/5 rounded-full blur-[150px]" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-amber-500/10 border border-amber-500/30 rounded-full text-amber-300 text-sm font-medium mb-4">
            <Ruler className="w-4 h-4" />
            Прозоре ціноутворення
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Тарифи доставки
          </h2>
          <p className="text-lg text-slate-300 max-w-2xl mx-auto">
            Оберіть оптимальний спосіб доставки залежно від ваги, термінів і бюджету
          </p>
        </div>

        {/* Interactive Slider */}
        <div className="max-w-4xl mx-auto mb-12 bg-gradient-to-br from-slate-900/80 via-slate-900/60 to-slate-800/80 border border-amber-500/20 rounded-3xl p-6 md:p-8 backdrop-blur-sm" data-testid="tariff-slider-widget">
          <div className="flex items-center justify-between flex-wrap gap-3 mb-2">
            <div>
              <h3 className="text-xl font-bold text-white flex items-center gap-2">
                <Calculator className="w-5 h-5 text-amber-400" />
                Розрахуйте свою вагу
              </h3>
              <p className="text-sm text-slate-400 mt-1">Перетягніть повзунок — ціни оновлюються миттєво</p>
            </div>
            <div className="flex items-center gap-2 px-4 py-2 bg-amber-500/15 border border-amber-500/30 rounded-xl">
              <span className="text-xs uppercase text-amber-300 tracking-widest">Вага</span>
              <span className="text-3xl font-bold text-white tabular-nums" data-testid="tariff-slider-weight">{weight}</span>
              <span className="text-amber-300 font-semibold">кг</span>
            </div>
          </div>

          <div className="relative pt-6 pb-2">
            <input
              type="range"
              min="1"
              max="1000"
              step="1"
              value={weight}
              onChange={(e) => setWeight(parseInt(e.target.value))}
              className="tariff-slider w-full"
              data-testid="tariff-slider-input"
              aria-label="Вага в кг"
            />
            <div className="flex justify-between text-xs text-slate-500 mt-2 px-1">
              <span>1 кг</span>
              <span>250 кг</span>
              <span>500 кг</span>
              <span>750 кг</span>
              <span>1000 кг</span>
            </div>
          </div>

          {/* Quick presets */}
          <div className="flex flex-wrap gap-2 mt-4">
            {[10, 50, 100, 300, 500, 1000].map(w => (
              <button
                key={w}
                onClick={() => setWeight(w)}
                className={`px-3 py-1.5 text-xs font-semibold rounded-lg border transition-all ${
                  weight === w
                    ? 'bg-amber-500/30 border-amber-400 text-amber-200'
                    : 'bg-slate-800/50 border-slate-700 text-slate-400 hover:border-amber-500/40 hover:text-amber-300'
                }`}
                data-testid={`weight-preset-${w}`}
              >
                {w} кг
              </button>
            ))}
          </div>

          {/* Result boxes */}
          <div className="grid grid-cols-2 gap-3 md:gap-4 mt-6">
            <div className={`relative p-4 md:p-5 rounded-2xl border-2 transition-all ${
              cheaper === 'air'
                ? 'bg-gradient-to-br from-cyan-500/20 to-cyan-700/10 border-cyan-400 shadow-[0_0_25px_-5px_rgba(6,182,212,0.6)]'
                : 'bg-slate-900/60 border-slate-700'
            }`} data-testid="slider-result-air">
              {cheaper === 'air' && (
                <span className="absolute -top-2.5 left-3 px-2.5 py-0.5 bg-cyan-500 text-white text-[10px] font-bold rounded-full uppercase tracking-wider">Вигідніше</span>
              )}
              <div className="flex items-center gap-2 mb-2">
                <Plane className={`w-4 h-4 ${cheaper === 'air' ? 'text-cyan-300' : 'text-slate-400'}`} />
                <span className={`text-xs uppercase font-semibold tracking-wide ${cheaper === 'air' ? 'text-cyan-300' : 'text-slate-400'}`}>Авіа · {air.delivery_time}</span>
              </div>
              <div className={`text-3xl md:text-4xl font-bold tabular-nums ${cheaper === 'air' ? 'text-white' : 'text-slate-300'}`}>
                ${airTotal.toFixed(2)}
              </div>
              <div className="text-xs text-slate-500 mt-1">${airTier?.price_per_kg || '—'}/кг × {weight} кг</div>
            </div>

            <div className={`relative p-4 md:p-5 rounded-2xl border-2 transition-all ${
              cheaper === 'sea'
                ? 'bg-gradient-to-br from-blue-500/20 to-blue-700/10 border-blue-400 shadow-[0_0_25px_-5px_rgba(59,130,246,0.6)]'
                : 'bg-slate-900/60 border-slate-700'
            }`} data-testid="slider-result-sea">
              {cheaper === 'sea' && (
                <span className="absolute -top-2.5 left-3 px-2.5 py-0.5 bg-blue-500 text-white text-[10px] font-bold rounded-full uppercase tracking-wider">Вигідніше</span>
              )}
              <div className="flex items-center gap-2 mb-2">
                <Ship className={`w-4 h-4 ${cheaper === 'sea' ? 'text-blue-300' : 'text-slate-400'}`} />
                <span className={`text-xs uppercase font-semibold tracking-wide ${cheaper === 'sea' ? 'text-blue-300' : 'text-slate-400'}`}>Море · {sea.delivery_time}</span>
              </div>
              <div className={`text-3xl md:text-4xl font-bold tabular-nums ${cheaper === 'sea' ? 'text-white' : 'text-slate-300'}`}>
                ${seaTotal.toFixed(2)}
              </div>
              <div className="text-xs text-slate-500 mt-1">${seaTier?.price_per_kg || '—'}/кг × {weight} кг</div>
            </div>
          </div>

          {cheaper && savings > 0.01 && (
            <div className="mt-4 text-center" data-testid="slider-savings">
              <span className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-500/15 border border-emerald-500/40 rounded-full text-emerald-300 text-sm font-semibold">
                <CheckCircle className="w-4 h-4" />
                Економія {cheaper === 'air' ? 'на авіа' : 'на морі'}: ${savings.toFixed(2)}
              </span>
            </div>
          )}

          {/* Discount Dwell Banner */}
          {cats.discount?.enabled && showDiscount && (() => {
            const pct = parseFloat(cats.discount.percent) || 0;
            const bestTotal = Math.min(airTotal, seaTotal);
            const discountedTotal = bestTotal * (1 - pct / 100);
            const savedNow = bestTotal - discountedTotal;
            return (
              <div className="mt-6 relative overflow-hidden rounded-2xl border border-amber-400/50 bg-gradient-to-r from-amber-500/15 via-orange-500/10 to-amber-500/15 p-5 animate-fadeIn" data-testid="tariff-discount-banner">
                <div className="absolute -top-12 -right-12 w-40 h-40 bg-amber-500/20 rounded-full blur-3xl pointer-events-none" />
                <div className="absolute -bottom-12 -left-12 w-40 h-40 bg-orange-500/20 rounded-full blur-3xl pointer-events-none" />
                <div className="relative flex flex-col md:flex-row items-center justify-between gap-4">
                  <div className="flex items-start gap-3 flex-1">
                    <div className="w-12 h-12 bg-gradient-to-br from-amber-400 to-orange-500 rounded-xl flex items-center justify-center text-white font-bold text-lg shadow-[0_0_20px_rgba(245,158,11,0.5)] flex-shrink-0">
                      −{pct}%
                    </div>
                    <div>
                      <h4 className="text-white font-bold text-base md:text-lg leading-tight">
                        {cats.discount.headline || `Отримайте знижку ${pct}%`}
                      </h4>
                      <p className="text-amber-200/80 text-sm mt-1">
                        {cats.discount.subtext}
                      </p>
                      <DiscountTimer />
                      {savedNow > 0.01 && (
                        <p className="text-emerald-300 text-sm font-semibold mt-1.5">
                          Зі знижкою для {weight} кг: ${discountedTotal.toFixed(2)} (економія ${savedNow.toFixed(2)})
                        </p>
                      )}
                    </div>
                  </div>
                  <Link to={cats.discount.cta_url || '/register'} className="flex-shrink-0" onClick={handleDiscountClick}>
                    <Button className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-400 hover:to-orange-400 text-white font-bold h-11 px-6 shadow-[0_0_20px_rgba(245,158,11,0.4)] hover:shadow-[0_0_30px_rgba(245,158,11,0.7)] transition-all" data-testid="tariff-discount-cta">
                      {cats.discount.cta_text || 'Замовити доставку'}
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </Link>
                  <button
                    onClick={() => setShowDiscount(false)}
                    className="absolute top-1 right-1 text-amber-300/60 hover:text-amber-200 p-1"
                    aria-label="Закрити"
                    data-testid="tariff-discount-close"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </div>
            );
          })()}
        </div>

        <div className="grid md:grid-cols-2 gap-8">
          {/* AIR */}
          <div className="group relative bg-gradient-to-br from-cyan-950/40 via-slate-900/60 to-blue-950/40 border border-cyan-500/30 rounded-3xl overflow-hidden hover:border-cyan-400/60 hover:shadow-[0_0_40px_-10px_rgba(6,182,212,0.5)] transition-all" data-testid="tariff-card-air">
            <div className="absolute top-0 right-0 w-40 h-40 bg-cyan-500/10 rounded-full blur-3xl group-hover:bg-cyan-400/20 transition-all" />
            <div className="relative p-7">
              <div className="flex items-start justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className="w-14 h-14 bg-gradient-to-br from-cyan-400 to-blue-500 rounded-2xl flex items-center justify-center shadow-[0_0_20px_rgba(6,182,212,0.4)]">
                    <Plane className="w-7 h-7 text-white" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-white">{air.label}</h3>
                    <p className="text-cyan-300 text-sm">Швидка доставка</p>
                  </div>
                </div>
                <div className="px-3 py-1.5 bg-cyan-500/15 border border-cyan-500/30 rounded-lg text-cyan-300 text-sm font-semibold whitespace-nowrap flex items-center gap-1">
                  <Clock className="w-3.5 h-3.5" />
                  {air.delivery_time}
                </div>
              </div>

              <p className="text-slate-300 mb-5 leading-relaxed">{air.description}</p>

              {/* Rates table */}
              <div className="bg-slate-950/50 rounded-xl border border-cyan-500/20 overflow-hidden mb-5">
                <div className="grid grid-cols-2 px-4 py-2.5 bg-cyan-500/10 border-b border-cyan-500/20 text-xs font-semibold uppercase tracking-wider text-cyan-300">
                  <div>Вага</div>
                  <div className="text-right">Ціна за кг</div>
                </div>
                {(air.rates || []).map((r, i) => {
                  const active = airTier && r.min_kg === airTier.min_kg && r.max_kg === airTier.max_kg;
                  return (
                  <div key={i} className={`grid grid-cols-2 px-4 py-3 border-b border-slate-800/50 last:border-b-0 transition-colors text-sm ${active ? 'bg-cyan-500/15 ring-1 ring-inset ring-cyan-400/40' : 'hover:bg-cyan-500/5'}`}>
                    <div className={`font-medium ${active ? 'text-cyan-200' : 'text-slate-200'}`}>
                      {r.min_kg} – {r.max_kg >= 9999 ? '∞' : r.max_kg} кг
                      {active && <span className="ml-2 text-[10px] text-cyan-300 uppercase tracking-wide">ваш тариф</span>}
                    </div>
                    <div className="text-right">
                      <span className={`text-lg font-bold ${active ? 'text-cyan-200' : 'text-cyan-300'}`}>${r.price_per_kg}</span>
                      <span className="text-slate-500 text-xs ml-1">/кг</span>
                    </div>
                  </div>
                  );
                })}
              </div>

              {/* Terms */}
              <div>
                <h4 className="text-sm font-semibold text-cyan-300 mb-2 uppercase tracking-wide">Умови</h4>
                <ul className="space-y-1.5">
                  {(air.terms || []).map((t, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-slate-300">
                      <CheckCircle className="w-4 h-4 text-cyan-400 flex-shrink-0 mt-0.5" />
                      <span>{t}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>

          {/* SEA */}
          <div className="group relative bg-gradient-to-br from-blue-950/40 via-slate-900/60 to-indigo-950/40 border border-blue-500/30 rounded-3xl overflow-hidden hover:border-blue-400/60 hover:shadow-[0_0_40px_-10px_rgba(59,130,246,0.5)] transition-all" data-testid="tariff-card-sea">
            <div className="absolute top-0 right-0 w-40 h-40 bg-blue-500/10 rounded-full blur-3xl group-hover:bg-blue-400/20 transition-all" />
            <div className="relative p-7">
              <div className="flex items-start justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className="w-14 h-14 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl flex items-center justify-center shadow-[0_0_20px_rgba(59,130,246,0.4)]">
                    <Ship className="w-7 h-7 text-white" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-white">{sea.label}</h3>
                    <p className="text-blue-300 text-sm">Економна доставка</p>
                  </div>
                </div>
                <div className="px-3 py-1.5 bg-blue-500/15 border border-blue-500/30 rounded-lg text-blue-300 text-sm font-semibold whitespace-nowrap flex items-center gap-1">
                  <Clock className="w-3.5 h-3.5" />
                  {sea.delivery_time}
                </div>
              </div>

              <p className="text-slate-300 mb-5 leading-relaxed">{sea.description}</p>

              <div className="bg-slate-950/50 rounded-xl border border-blue-500/20 overflow-hidden mb-5">
                <div className="grid grid-cols-2 px-4 py-2.5 bg-blue-500/10 border-b border-blue-500/20 text-xs font-semibold uppercase tracking-wider text-blue-300">
                  <div>Вага</div>
                  <div className="text-right">Ціна за кг</div>
                </div>
                {(sea.rates || []).map((r, i) => {
                  const active = seaTier && r.min_kg === seaTier.min_kg && r.max_kg === seaTier.max_kg;
                  return (
                  <div key={i} className={`grid grid-cols-2 px-4 py-3 border-b border-slate-800/50 last:border-b-0 transition-colors text-sm ${active ? 'bg-blue-500/15 ring-1 ring-inset ring-blue-400/40' : 'hover:bg-blue-500/5'}`}>
                    <div className={`font-medium ${active ? 'text-blue-200' : 'text-slate-200'}`}>
                      {r.min_kg} – {r.max_kg >= 9999 ? '∞' : r.max_kg} кг
                      {active && <span className="ml-2 text-[10px] text-blue-300 uppercase tracking-wide">ваш тариф</span>}
                    </div>
                    <div className="text-right">
                      <span className={`text-lg font-bold ${active ? 'text-blue-200' : 'text-blue-300'}`}>${r.price_per_kg}</span>
                      <span className="text-slate-500 text-xs ml-1">/кг</span>
                    </div>
                  </div>
                  );
                })}
              </div>

              <div>
                <h4 className="text-sm font-semibold text-blue-300 mb-2 uppercase tracking-wide">Умови</h4>
                <ul className="space-y-1.5">
                  {(sea.terms || []).map((t, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-slate-300">
                      <CheckCircle className="w-4 h-4 text-blue-400 flex-shrink-0 mt-0.5" />
                      <span>{t}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>

        <p className="text-center text-slate-400 text-sm mt-10">
          Тарифи можуть змінюватися. Точну вартість уточнюйте у менеджера або через калькулятор.
        </p>
      </div>
    </section>
  );
};
